// ============================================================
// /square/projects/:id/comms — Project Communications
//
// Premium dark collaboration surface. Channels, people,
// workspace, files, timeline, DMs, and message composer.
// Project-scoped — distinct from Comms Ops (operator rail).
//
// Data: local state for now; persistence layer next phase.
// Architecture: ready for sq_project_messages or similar.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import ProjectSurface from './ProjectSurface'
import { useProject } from '../../hooks/useProjects'
import { SHELL_TOKENS, text } from '../../shell/styles'
import { getRuntime } from '../../hooks/_mode'

// --- Types ---

type CommsTab = 'channels' | 'people' | 'workspace' | 'files' | 'timeline'

interface Channel {
  id: string
  name: string
  type: 'public' | 'private' | 'dm'
  unread: number
  lastMessage?: string
}

interface Person {
  id: string
  name: string
  role: string
  status: 'online' | 'away' | 'offline'
  avatar: string
}

interface Message {
  id: string
  sender: string
  senderRole: string
  body: string
  created_at: string
  channel: string
}

interface FileItem {
  id: string
  name: string
  type: string
  size: string
  uploaded_at: string
  uploaded_by: string
}

interface TimelineEvent {
  id: string
  action: string
  actor: string
  detail: string
  created_at: string
}

// --- Project color identity ---
const PROJECT_COLORS = [
  '#7c5cfc', '#0891b2', '#059669', '#dc2626', '#f59e0b',
  '#6366f1', '#14b8a6', '#f43f5e', '#f97316', '#8b5cf6',
]

function projectColor(id?: string): string {
  if (!id) return PROJECT_COLORS[0]
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0
  return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length]
}

export default function ProjectComms() {
  const { id } = useParams<{ id: string }>()
  const { project } = useProject(id)
  const [activeTab, setActiveTab] = useState<CommsTab>('channels')
  const [activeChannel, setActiveChannel] = useState<string>('general')
  const [messages, setMessages] = useState<Message[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const color = projectColor(id)

  // Seed channels for this project
  const channels: Channel[] = [
    { id: 'general', name: 'General', type: 'public', unread: 0 },
    { id: 'updates', name: 'Updates', type: 'public', unread: 0 },
    { id: 'internal', name: 'Internal', type: 'private', unread: 0 },
    { id: 'deliverables', name: 'Deliverables', type: 'public', unread: 0 },
  ]

  const people: Person[] = [
    { id: '1', name: 'Operator', role: 'Admin', status: 'online', avatar: 'O' },
  ]

  const files: FileItem[] = []
  const timeline: TimelineEvent[] = []

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function handleSend() {
    if (!draft.trim() || sending) return
    setSending(true)
    const newMsg: Message = {
      id: `msg-${Date.now()}`,
      sender: 'Operator',
      senderRole: 'Admin',
      body: draft.trim(),
      created_at: new Date().toISOString(),
      channel: activeChannel,
    }
    setMessages((prev) => [...prev, newMsg])
    setDraft('')
    setSending(false)
  }

  const channelMessages = messages.filter((m) => m.channel === activeChannel)
  const currentChannel = channels.find((c) => c.id === activeChannel)

  const TABS: { id: CommsTab; label: string; count?: number }[] = [
    { id: 'channels', label: 'Channels', count: channels.length },
    { id: 'people', label: 'People', count: people.length },
    { id: 'workspace', label: 'Workspace' },
    { id: 'files', label: 'Files', count: files.length },
    { id: 'timeline', label: 'Timeline' },
  ]

  return (
    <ProjectSurface
      kicker="PROJECT · COMMS"
      title="Communications"
      description="Project channels, people, workspace, and files."
    >
      {/* Project identity bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
        padding: '12px 16px', borderRadius: 12,
        background: `${color}08`, border: `1px solid ${color}20`,
      }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%', background: color,
          boxShadow: `0 0 8px ${color}60`,
        }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.ink }}>
          {project?.name || 'Project'}
        </div>
        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted }}>
          {project?.status || 'active'} · {channels.length} channels · {people.length} members
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, overflowX: 'auto' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: activeTab === tab.id
                ? `1px solid ${color}40`
                : '1px solid rgba(255,255,255,0.06)',
              background: activeTab === tab.id
                ? `${color}12`
                : 'rgba(255,255,255,0.02)',
              color: activeTab === tab.id ? color : SHELL_TOKENS.inkDim,
              fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)',
              letterSpacing: '0.04em', cursor: 'pointer',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{
                marginLeft: 6, fontSize: 9, opacity: 0.7,
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Channels tab */}
      {activeTab === 'channels' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '180px 1fr',
          gap: 10,
          height: 420,
        }}>
          {/* Channel list */}
          <div style={{
            background: SHELL_TOKENS.surface,
            border: `1px solid ${SHELL_TOKENS.border}`,
            borderRadius: 12,
            padding: 6,
            overflowY: 'auto',
          }}>
            {channels.map((ch) => (
              <button
                key={ch.id}
                type="button"
                onClick={() => setActiveChannel(ch.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', textAlign: 'left',
                  padding: '10px 10px',
                  borderRadius: 8,
                  border: activeChannel === ch.id
                    ? `1px solid ${color}30`
                    : '1px solid transparent',
                  background: activeChannel === ch.id
                    ? `${color}0a`
                    : 'transparent',
                  color: activeChannel === ch.id ? SHELL_TOKENS.ink : SHELL_TOKENS.inkDim,
                  fontSize: 12, fontWeight: activeChannel === ch.id ? 700 : 500,
                  fontFamily: 'var(--font-mono)',
                  cursor: 'pointer',
                  transition: 'all 0.12s ease',
                  marginBottom: 2,
                }}
              >
                <span style={{
                  fontSize: 9, color: ch.type === 'private' ? SHELL_TOKENS.amber : color,
                }}>
                  {ch.type === 'private' ? '🔒' : '#'}
                </span>
                {ch.name}
                {ch.unread > 0 && (
                  <span style={{
                    marginLeft: 'auto', padding: '1px 5px', borderRadius: 8,
                    background: color, color: '#fff', fontSize: 9, fontWeight: 700,
                  }}>
                    {ch.unread}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Message thread */}
          <div style={{
            background: SHELL_TOKENS.surface,
            border: `1px solid ${SHELL_TOKENS.border}`,
            borderRadius: 12,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Channel header */}
            <div style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${SHELL_TOKENS.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color, fontSize: 12 }}>
                  {currentChannel?.type === 'private' ? '🔒' : '#'}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                  {currentChannel?.name || 'Channel'}
                </span>
              </div>
              <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint }}>
                {channelMessages.length} messages
              </span>
            </div>

            {/* Messages */}
            <div style={{
              flex: 1, padding: '12px 16px', overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              {channelMessages.length === 0 ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 8,
                }}>
                  <div style={{ fontSize: 24, color: `${color}40` }}>#</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.inkDim }}>
                    No messages in #{currentChannel?.name}
                  </div>
                  <div style={{
                    fontSize: 11, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)',
                    textAlign: 'center', maxWidth: 300,
                  }}>
                    Start a conversation. Messages are project-scoped and visible to team members.
                  </div>
                </div>
              ) : (
                channelMessages.map((msg) => {
                  const isOperator = msg.senderRole === 'Admin'
                  const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  return (
                    <div key={msg.id} style={{ display: 'flex', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: isOperator ? `${color}20` : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${isOperator ? `${color}30` : SHELL_TOKENS.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                        color: isOperator ? color : SHELL_TOKENS.inkDim,
                        flexShrink: 0,
                      }}>
                        {msg.sender[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                            {msg.sender}
                          </span>
                          <span style={{
                            fontSize: 8, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                          }}>
                            {msg.senderRole}
                          </span>
                          <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint }}>
                            {time}
                          </span>
                        </div>
                        <div style={{
                          fontSize: 13, color: SHELL_TOKENS.ink, lineHeight: 1.55,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {msg.body}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div style={{
              borderTop: `1px solid ${SHELL_TOKENS.border}`,
              padding: '10px 14px',
              display: 'flex', gap: 10, alignItems: 'flex-end',
            }}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend() }
                }}
                placeholder={`Message #${currentChannel?.name || 'channel'}...`}
                rows={1}
                style={{
                  flex: 1, resize: 'none',
                  border: `1px solid ${SHELL_TOKENS.border}`,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.03)',
                  color: SHELL_TOKENS.ink,
                  padding: '10px 14px',
                  fontSize: 13, fontFamily: 'var(--font-mono)',
                  outline: 'none', minHeight: 42,
                }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!draft.trim() || sending}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: draft.trim()
                    ? `linear-gradient(135deg, ${color}, ${color}99)`
                    : SHELL_TOKENS.surface,
                  color: draft.trim() ? '#fff' : SHELL_TOKENS.inkMuted,
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                  textTransform: 'uppercase', fontFamily: 'var(--font-mono)',
                  cursor: draft.trim() ? 'pointer' : 'not-allowed',
                  boxShadow: draft.trim() ? `0 2px 12px ${color}40` : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* People tab */}
      {activeTab === 'people' && (
        <div style={{
          background: SHELL_TOKENS.surface,
          border: `1px solid ${SHELL_TOKENS.border}`,
          borderRadius: 12, padding: 16,
        }}>
          {people.map((p) => (
            <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 0', borderBottom: `1px solid ${SHELL_TOKENS.border}`,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: `${color}15`, border: `1px solid ${color}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color,
              }}>
                {p.avatar}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.ink }}>{p.name}</div>
                <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted }}>{p.role}</div>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5,
                fontSize: 9, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint,
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: p.status === 'online' ? '#059669' : p.status === 'away' ? '#f59e0b' : SHELL_TOKENS.inkFaint,
                }} />
                {p.status}
              </div>
            </div>
          ))}
          <div style={{
            marginTop: 16, padding: '14px', borderRadius: 10,
            background: `${color}06`, border: `1px solid ${color}15`,
            fontSize: 11, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)',
            textAlign: 'center',
          }}>
            Invite team members from project settings to add people to this workspace.
          </div>
        </div>
      )}

      {/* Workspace tab */}
      {activeTab === 'workspace' && (
        <div style={{
          background: SHELL_TOKENS.surface,
          border: `1px solid ${SHELL_TOKENS.border}`,
          borderRadius: 12, padding: 24, textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, color: `${color}60`, marginBottom: 10 }}>◇</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: SHELL_TOKENS.ink, marginBottom: 6 }}>
            Project Workspace
          </div>
          <div style={{ fontSize: 12, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', maxWidth: 400, margin: '0 auto' }}>
            Shared project workspace — documents, notes, and collaborative content. Open Content Studio to create and manage workspace items.
          </div>
        </div>
      )}

      {/* Files tab */}
      {activeTab === 'files' && (
        <div style={{
          background: SHELL_TOKENS.surface,
          border: `1px solid ${SHELL_TOKENS.border}`,
          borderRadius: 12, padding: 24, textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, color: `${color}60`, marginBottom: 10 }}>▢</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: SHELL_TOKENS.ink, marginBottom: 6 }}>
            Project Files
          </div>
          <div style={{ fontSize: 12, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', maxWidth: 400, margin: '0 auto' }}>
            Deliverables, evidence, and shared assets scoped to this project. Upload files from the Files surface in Command Center.
          </div>
        </div>
      )}

      {/* Timeline tab */}
      {activeTab === 'timeline' && (
        <div style={{
          background: SHELL_TOKENS.surface,
          border: `1px solid ${SHELL_TOKENS.border}`,
          borderRadius: 12, padding: 24, textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, color: `${color}60`, marginBottom: 10 }}>◉</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: SHELL_TOKENS.ink, marginBottom: 6 }}>
            Project Timeline
          </div>
          <div style={{ fontSize: 12, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', maxWidth: 400, margin: '0 auto' }}>
            Activity feed — deployments, status changes, deliverables, and milestone events. Events will populate as work progresses.
          </div>
        </div>
      )}
    </ProjectSurface>
  )
}
